<template>
  <div class="dossier-infra">
    <div class="dossier-infra__wrapper">
      <!-- ─── Cabeçalho ─── -->
      <div class="dossier-infra__header" @click="toggleCollapse">
        <div class="dossier-infra__header-left">
          <span class="dossier-infra__state-icon" :class="headerStateClass">
            <i :class="headerStateIcon"></i>
          </span>
          <div>
            <h3 class="dossier-infra__title">Infraestrutura da ILPI</h3>
            <span v-if="isCollapsed" class="dossier-infra__subtitle">
              Estrutura física, ambientes, elevador e documentos técnicos
            </span>
          </div>
        </div>
        <button
          class="dossier-infra__toggle"
          type="button"
          :aria-expanded="!isCollapsed"
        >
          <i
            class="fas"
            :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
          ></i>
        </button>
      </div>

      <!-- ─── Corpo ─── -->
      <div v-show="!isCollapsed" class="dossier-infra__body">
        <!-- Explicativo -->
        <div v-if="!isViewMode" class="dossier-infra__explainer">
          <p>
            Organiza as informações estruturais da ILPI para gestão interna e
            apresentação à fiscalização. O Sistema Clin não valida conteúdo
            técnico — apenas registra, organiza anexos e controla datas de
            validade.
          </p>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             BLOCO 1 — Identificação básica da estrutura
             ═══════════════════════════════════════════════════════ -->
        <div class="dossier-infra__section">
          <div class="dossier-infra__section-title">
            <i class="fas fa-building mr-1"></i>
            1. Identificação básica
          </div>

          <div class="dossier-module__fields">
            <!-- Capacidade instalada autorizada -->
            <div
              class="dossier-module__field-group dossier-module__field-group--small"
            >
              <label
                class="dossier-module__label dossier-module__label--required"
              >
                Capacidade autorizada (leitos)
              </label>
              <v-text-field
                v-if="!isViewMode"
                v-model.number="formData.authorized_capacity"
                type="number"
                min="1"
                dense
                outlined
                hide-details="auto"
                :rules="[rules.required, rules.positive]"
                class="dossier-module__input"
              />
              <span v-else class="dossier-module__view-value">{{
                formData.authorized_capacity || "—"
              }}</span>
            </div>

            <!-- Ocupação atual (busca automática via API) -->
            <div
              class="dossier-module__field-group dossier-module__field-group--small"
            >
              <label class="dossier-module__label"
                >Ocupação atual (residentes)</label
              >
              <div class="dossier-infra__auto-value">
                <span v-if="loadingOccupancy">
                  <i class="fas fa-spinner fa-spin mr-1"></i>Carregando...
                </span>
                <span v-else class="dossier-infra__occupancy">
                  {{
                    formData.current_occupancy !== null
                      ? formData.current_occupancy
                      : "—"
                  }}
                  <span
                    v-if="formData.authorized_capacity"
                    class="dossier-infra__occupancy-total"
                  >
                    / {{ formData.authorized_capacity }}
                  </span>
                </span>
                <span class="dossier-infra__auto-badge">automático</span>
              </div>
            </div>

            <!-- Perfil de atendimento -->
            <div class="dossier-module__field-group">
              <label
                class="dossier-module__label dossier-module__label--required"
              >
                Perfil de atendimento
              </label>
              <div v-if="!isViewMode" class="dossier-infra__checkboxes">
                <label
                  v-for="g in ['Grau I', 'Grau II', 'Grau III']"
                  :key="g"
                  class="dossier-infra__checkbox-item"
                >
                  <input
                    type="checkbox"
                    :value="g"
                    v-model="formData.care_profiles"
                    class="dossier-infra__checkbox"
                  />
                  <span>{{ g }}</span>
                </label>
              </div>
              <span v-else class="dossier-module__view-value">
                {{ formData.care_profiles.join(", ") || "—" }}
              </span>
            </div>
          </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             BLOCO 2 — Pavimentos e elevador
             ═══════════════════════════════════════════════════════ -->
        <div class="dossier-infra__section">
          <div class="dossier-infra__section-title">
            <i class="fas fa-layer-group mr-1"></i>
            2. Pavimentos e elevador
          </div>

          <!-- Mais de um pavimento? -->
          <div class="dossier-infra__field-group">
            <label
              class="dossier-module__label dossier-module__label--required"
            >
              A ILPI possui mais de um pavimento?
            </label>
            <div class="dossier-infra__radios">
              <label
                v-for="opt in ['Sim', 'Não']"
                :key="opt"
                class="dossier-infra__radio-item"
              >
                <input
                  type="radio"
                  :value="opt"
                  v-model="formData.multiple_floors"
                  :disabled="isViewMode"
                  class="dossier-infra__radio"
                />
                <span>{{ opt }}</span>
              </label>
            </div>
          </div>

          <!-- Sub-bloco elevador (só se Sim) -->
          <template v-if="formData.multiple_floors === 'Sim'">
            <div class="dossier-infra__field-group">
              <label
                class="dossier-module__label dossier-module__label--required"
              >
                A ILPI possui elevador?
              </label>
              <div class="dossier-infra__radios">
                <label
                  v-for="opt in ['Sim', 'Não']"
                  :key="opt"
                  class="dossier-infra__radio-item"
                >
                  <input
                    type="radio"
                    :value="opt"
                    v-model="formData.has_elevator"
                    :disabled="isViewMode"
                    class="dossier-infra__radio"
                  />
                  <span>{{ opt }}</span>
                </label>
              </div>
            </div>

            <template v-if="formData.has_elevator === 'Sim'">
              <!-- Identificação do elevador -->
              <div class="dossier-module__fields" style="margin-top: 10px">
                <div class="dossier-module__field-group">
                  <label class="dossier-module__label">Tipo de elevador</label>
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="formData.elevator.type"
                    dense
                    outlined
                    hide-details
                    placeholder="Ex: Elétrico, Hidráulico"
                    class="dossier-module__input"
                  />
                  <span v-else class="dossier-module__view-value">{{
                    formData.elevator.type || "—"
                  }}</span>
                </div>
                <div
                  class="dossier-module__field-group dossier-module__field-group--small"
                >
                  <label class="dossier-module__label">Capacidade</label>
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="formData.elevator.capacity"
                    dense
                    outlined
                    hide-details
                    placeholder="Ex: 450kg / 6 pessoas"
                    class="dossier-module__input"
                  />
                  <span v-else class="dossier-module__view-value">{{
                    formData.elevator.capacity || "—"
                  }}</span>
                </div>
                <div
                  class="dossier-module__field-group dossier-module__field-group--small"
                >
                  <label class="dossier-module__label">Fabricante</label>
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="formData.elevator.manufacturer"
                    dense
                    outlined
                    hide-details
                    class="dossier-module__input"
                  />
                  <span v-else class="dossier-module__view-value">{{
                    formData.elevator.manufacturer || "—"
                  }}</span>
                </div>
              </div>

              <!-- Documento do elevador (usa DossierDocumentCard) -->
              <DossierDocumentCard
                title="Alvará / Licença do Elevador"
                :status-options="elevatorDocStatus"
                :multiple-status="true"
                :status-requires-validity="[
                  'possui_vigente',
                  'possui_provisorio',
                ]"
                :status-requires-upload="[
                  'possui_vigente',
                  'possui_provisorio',
                ]"
                explainer-text=""
                :mode="mode"
                :not-applicable="false"
                :start-collapsed="false"
                v-model="formData.elevator.doc"
                save-endpoint="/rest-home/dossier/infrastructure/elevator/doc"
                upload-endpoint="/rest-home/dossier/infrastructure/elevator/doc/upload"
                @saved="
                  (d) =>
                    $emit('saved', {
                      module: 'infrastructure',
                      block: 'elevator_doc',
                      data: d,
                    })
                "
              />

              <!-- Manutenção do elevador -->
              <div class="dossier-infra__subsection">
                <div class="dossier-infra__subsection-title">
                  Manutenção do elevador
                </div>
                <div class="dossier-module__fields">
                  <div class="dossier-module__field-group">
                    <label class="dossier-module__label"
                      >Empresa responsável</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.elevator.maintenance_company"
                      dense
                      outlined
                      hide-details
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.elevator.maintenance_company || "—"
                    }}</span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label">CNPJ da empresa</label>
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.elevator.maintenance_cnpj"
                      v-mask="'##.###.###/####-##'"
                      dense
                      outlined
                      hide-details
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.elevator.maintenance_cnpj || "—"
                    }}</span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label"
                      >Última manutenção</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.elevator.last_maintenance"
                      type="date"
                      dense
                      outlined
                      hide-details
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">
                      {{ formatDateBR(formData.elevator.last_maintenance) }}
                    </span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label"
                      >Próxima manutenção</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.elevator.next_maintenance"
                      type="date"
                      dense
                      outlined
                      hide-details
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">
                      {{ formatDateBR(formData.elevator.next_maintenance) }}
                    </span>
                  </div>
                </div>

                <!-- Contrato de manutenção -->
                <DossierDocumentCard
                  title="Contrato de Manutenção do Elevador"
                  :status-options="maintenanceContractStatus"
                  :multiple-status="true"
                  :status-requires-validity="['possui_vigente']"
                  :status-requires-upload="['possui_vigente']"
                  explainer-text=""
                  :mode="mode"
                  :not-applicable="false"
                  :start-collapsed="false"
                  v-model="formData.elevator.maintenance_contract"
                  save-endpoint="/rest-home/dossier/infrastructure/elevator/maintenance"
                  upload-endpoint="/rest-home/dossier/infrastructure/elevator/maintenance/upload"
                  @saved="
                    (d) =>
                      $emit('saved', {
                        module: 'infrastructure',
                        block: 'elevator_maintenance',
                        data: d,
                      })
                  "
                />
              </div>
            </template>
          </template>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             BLOCO 3 — Ambientes da ILPI
             ═══════════════════════════════════════════════════════ -->
        <div class="dossier-infra__section">
          <div class="dossier-infra__section-title">
            <i class="fas fa-door-open mr-1"></i>
            3. Ambientes da ILPI
          </div>

          <!-- Seleção de ambientes -->
          <div v-if="!isViewMode" class="dossier-infra__field-group">
            <label
              class="dossier-module__label dossier-module__label--required"
            >
              Quais ambientes a ILPI possui?
            </label>
            <div class="dossier-infra__rooms-grid">
              <label
                v-for="room in roomOptions"
                :key="room.value"
                class="dossier-infra__room-item"
              >
                <input
                  type="checkbox"
                  :value="room.value"
                  v-model="formData.rooms"
                  class="dossier-infra__checkbox"
                />
                <span>{{ room.label }}</span>
              </label>
            </div>

            <!-- Campo "Outros" -->
            <div
              v-if="formData.rooms.includes('outros')"
              class="dossier-infra__others-field"
            >
              <label class="dossier-module__label"
                >Outros ambientes (separe por "/")</label
              >
              <v-text-field
                v-model="formData.rooms_others"
                dense
                outlined
                hide-details
                placeholder="Ex: Sala de fisioterapia / Jardim interno"
                class="dossier-module__input"
              />
            </div>
          </div>

          <!-- Sub-blocos por ambiente marcado -->
          <div
            v-for="room in selectedRooms"
            :key="room.value"
            class="dossier-infra__room-block"
          >
            <div class="dossier-infra__room-block-title">
              <i class="fas fa-angle-right mr-1"></i>
              {{ room.label }}
            </div>

            <div class="dossier-module__fields">
              <!-- Quantidade -->
              <div
                class="dossier-module__field-group dossier-module__field-group--xsmall"
              >
                <label class="dossier-module__label">Qtd.</label>
                <v-text-field
                  v-if="!isViewMode"
                  v-model.number="getRoomData(room.value).quantity"
                  type="number"
                  min="1"
                  dense
                  outlined
                  hide-details
                  class="dossier-module__input"
                />
                <span v-else class="dossier-module__view-value">{{
                  getRoomData(room.value).quantity || "—"
                }}</span>
              </div>

              <!-- Status do ambiente -->
              <div class="dossier-module__field-group">
                <label
                  class="dossier-module__label dossier-module__label--required"
                >
                  Situação
                </label>
                <v-select
                  v-if="!isViewMode"
                  v-model="getRoomData(room.value).status"
                  :items="roomStatusOptions"
                  dense
                  outlined
                  hide-details
                  class="dossier-module__input"
                />
                <span v-else class="dossier-module__view-value">{{
                  getRoomData(room.value).status || "—"
                }}</span>
              </div>

              <!-- Observações -->
              <div class="dossier-module__field-group">
                <label class="dossier-module__label">Observações</label>
                <v-text-field
                  v-if="!isViewMode"
                  v-model="getRoomData(room.value).observations"
                  dense
                  outlined
                  hide-details
                  placeholder="Informações complementares"
                  class="dossier-module__input"
                />
                <span v-else class="dossier-module__view-value">{{
                  getRoomData(room.value).observations || "—"
                }}</span>
              </div>
            </div>

            <!-- Upload de evidências do ambiente -->
            <div v-if="!isViewMode" class="dossier-infra__room-uploads">
              <label class="dossier-module__label"
                >Evidências (fotos, planta ou laudo — PDF)</label
              >
              <div
                v-if="getRoomData(room.value).file_url"
                class="dossier-infra__file-attached"
              >
                <i class="fas fa-file-pdf dossier-infra__file-icon"></i>
                <span>{{
                  getRoomData(room.value).file_name || "Arquivo anexado"
                }}</span>
                <a
                  :href="getRoomData(room.value).file_url"
                  target="_blank"
                  class="dossier-infra__file-link ml-2"
                >
                  <i class="fas fa-external-link-alt mr-1"></i>Ver
                </a>
                <v-btn
                  x-small
                  text
                  class="btn-notcertified ml-2"
                  @click="triggerRoomUpload(room.value)"
                >
                  <i class="fas fa-sync-alt mr-1"></i>Substituir
                </v-btn>
              </div>
              <v-btn
                v-else
                small
                class="btn-add"
                :loading="getRoomData(room.value)._uploading"
                @click="triggerRoomUpload(room.value)"
              >
                <i class="fas fa-upload mr-1"></i>Anexar evidência
              </v-btn>
              <input
                :ref="`roomUpload_${room.value}`"
                type="file"
                accept="application/pdf,image/*"
                style="display: none"
                @change="(e) => handleRoomUpload(e, room.value)"
              />
            </div>
            <div v-else-if="getRoomData(room.value).file_url">
              <a
                :href="getRoomData(room.value).file_url"
                target="_blank"
                class="dossier-infra__file-link"
              >
                <i class="fas fa-file-pdf mr-1"></i>Ver evidências
              </a>
            </div>
          </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             BLOCO 4 — Acessibilidade e segurança
             ═══════════════════════════════════════════════════════ -->
        <div class="dossier-infra__section">
          <div class="dossier-infra__section-title">
            <i class="fas fa-wheelchair mr-1"></i>
            4. Acessibilidade e segurança
          </div>

          <div class="dossier-infra__accessibility-grid">
            <div
              v-for="item in accessibilityItems"
              :key="item.key"
              class="dossier-infra__access-item"
            >
              <label class="dossier-module__label">{{ item.label }}</label>
              <v-select
                v-if="!isViewMode"
                v-model="formData.accessibility[item.key]"
                :items="['Sim', 'Não', 'Em adequação']"
                dense
                outlined
                hide-details
                class="dossier-module__input"
              />
              <span
                v-else
                class="dossier-module__view-value"
                :class="accessibilityClass(formData.accessibility[item.key])"
              >
                {{ formData.accessibility[item.key] || "—" }}
              </span>
            </div>
          </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             BLOCO 5 — Documentos técnicos (opcionais)
             ═══════════════════════════════════════════════════════ -->
        <div class="dossier-infra__section">
          <div class="dossier-infra__section-title">
            <i class="fas fa-file-alt mr-1"></i>
            5. Documentos técnicos (quando existirem)
          </div>

          <DossierDocumentCard
            v-for="techDoc in technicalDocs"
            :key="techDoc.key"
            :title="techDoc.label"
            :status-options="techDocStatus"
            :multiple-status="true"
            :status-requires-validity="['possui_vigente', 'possui_provisorio']"
            :status-requires-upload="['possui_vigente', 'possui_provisorio']"
            explainer-text=""
            :mode="mode"
            :not-applicable="false"
            :start-collapsed="true"
            v-model="formData.tech_docs[techDoc.key]"
            :save-endpoint="`/rest-home/dossier/infrastructure/tech-doc/${techDoc.key}`"
            :upload-endpoint="`/rest-home/dossier/infrastructure/tech-doc/${techDoc.key}/upload`"
            @saved="
              (d) =>
                $emit('saved', {
                  module: 'infrastructure',
                  block: techDoc.key,
                  data: d,
                })
            "
          />

          <!-- Outros documentos técnicos (campo livre) -->
          <div v-if="!isViewMode" class="dossier-infra__add-doc">
            <v-btn small class="btn-add" @click="addOtherTechDoc">
              <i class="fas fa-plus mr-1"></i>Adicionar outro documento técnico
            </v-btn>
          </div>

          <div
            v-for="(extra, idx) in formData.extra_tech_docs"
            :key="`extra_${idx}`"
            class="dossier-infra__extra-doc"
          >
            <div class="dossier-module__fields">
              <div class="dossier-module__field-group">
                <label class="dossier-module__label">Nome do documento</label>
                <v-text-field
                  v-if="!isViewMode"
                  v-model="extra.name"
                  dense
                  outlined
                  hide-details
                  placeholder="Ex: Laudo de acessibilidade"
                  class="dossier-module__input"
                />
                <span v-else class="dossier-module__view-value">{{
                  extra.name || "—"
                }}</span>
              </div>
            </div>
            <DossierDocumentCard
              v-if="extra.name"
              :title="extra.name || 'Documento técnico adicional'"
              :status-options="techDocStatus"
              :multiple-status="true"
              :status-requires-validity="[
                'possui_vigente',
                'possui_provisorio',
              ]"
              :status-requires-upload="['possui_vigente', 'possui_provisorio']"
              explainer-text=""
              :mode="mode"
              :not-applicable="false"
              :start-collapsed="false"
              v-model="extra.doc"
              :save-endpoint="`/rest-home/dossier/infrastructure/extra-doc/${idx}`"
              :upload-endpoint="`/rest-home/dossier/infrastructure/extra-doc/${idx}/upload`"
            />
            <v-btn
              v-if="!isViewMode"
              x-small
              text
              class="btn-notcertified mt-1"
              @click="formData.extra_tech_docs.splice(idx, 1)"
            >
              <i class="fas fa-trash-alt mr-1"></i>Remover
            </v-btn>
          </div>
        </div>

        <!-- ─── Botão salvar metadados ─── -->
        <div v-if="!isViewMode" class="dossier-infra__save">
          <v-btn class="btn-add" :loading="saveLoading" @click="save">
            <i class="fas fa-save mr-1"></i>
            Salvar infraestrutura
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

const emptyDoc = () => ({
  status: [],
  validity_date: null,
  file_url: null,
  file_name: null,
  observations: "",
});

const emptyRoomData = () => ({
  quantity: null,
  status: null,
  observations: "",
  file_url: null,
  file_name: null,
  _uploading: false,
});

const ROOM_OPTIONS = [
  { value: "almoxarifado", label: "Almoxarifado" },
  { value: "area_externa", label: "Área externa" },
  { value: "banheiros_chuveiros", label: "Banheiros com chuveiros" },
  { value: "cinema", label: "Cinema" },
  { value: "cozinha", label: "Cozinha" },
  { value: "despensa", label: "Despensa" },
  { value: "dormitorios", label: "Dormitórios" },
  { value: "dormitorios_suite", label: "Dormitórios com banheiro (suíte)" },
  { value: "lavabo_moradores", label: "Lavabo para moradores" },
  { value: "lavabo_visitante", label: "Lavabo para visitantes" },
  { value: "lavanderia", label: "Lavanderia" },
  { value: "recepcao", label: "Recepção / Administração" },
  { value: "refeitorio", label: "Refeitório" },
  { value: "consultorio", label: "Sala de atendimento / Consultório" },
  { value: "sala_convivencia", label: "Sala de convivência" },
  { value: "enfermaria", label: "Sala de curativos / Enfermaria" },
  { value: "teatro", label: "Teatro" },
  { value: "outros", label: "Outros" },
];

const TECHNICAL_DOCS = [
  { key: "laudo_eletrico", label: "Laudo elétrico" },
  { key: "laudo_gas", label: "Laudo de gás" },
  { key: "extintores", label: "Manutenção de extintores" },
];

export default {
  name: "DossierInfrastructure",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },

  data() {
    const d = this.initialData || {};
    return {
      isCollapsed: this.startCollapsed,
      saveLoading: false,
      loadingOccupancy: false,
      roomOptions: ROOM_OPTIONS,
      technicalDocs: TECHNICAL_DOCS,

      elevatorDocStatus: [
        { value: "possui_vigente", label: "Possui alvará/licença vigente" },
        {
          value: "possui_provisorio",
          label: "Possui alvará/licença provisório",
        },
        { value: "em_elaboracao", label: "Em processo de renovação" },
        { value: "nao_possui", label: "Não possui" },
      ],
      maintenanceContractStatus: [
        { value: "possui_vigente", label: "Possui contrato vigente" },
        { value: "nao_possui", label: "Não possui contrato" },
      ],
      techDocStatus: [
        { value: "possui_vigente", label: "Possui documento vigente" },
        { value: "possui_provisorio", label: "Possui documento provisório" },
        { value: "em_elaboracao", label: "Em processo de renovação" },
        { value: "nao_possui", label: "Não possui" },
      ],
      roomStatusOptions: [
        "Adequado e em funcionamento",
        "Adequado com restrições",
        "Em adequação",
        "Em manutenção",
        "Não aplicável",
      ],
      accessibilityItems: [
        { key: "main_access", label: "Acessibilidade nos acessos principais" },
        { key: "handrails", label: "Corrimãos onde aplicável" },
        {
          key: "emergency_signage",
          label: "Sinalização de emergência onde aplicável",
        },
      ],

      formData: {
        // Bloco 1
        authorized_capacity: d.authorized_capacity || null,
        current_occupancy: d.current_occupancy || null,
        care_profiles: d.care_profiles || [],
        // Bloco 2
        multiple_floors: d.multiple_floors || null,
        has_elevator: d.has_elevator || null,
        elevator: {
          type: "",
          capacity: "",
          manufacturer: "",
          last_maintenance: null,
          next_maintenance: null,
          maintenance_company: "",
          maintenance_cnpj: "",
          doc: emptyDoc(),
          maintenance_contract: emptyDoc(),
          ...(d.elevator || {}),
        },
        // Bloco 3
        rooms: d.rooms || [],
        rooms_others: d.rooms_others || "",
        rooms_data: d.rooms_data || {},
        // Bloco 4
        accessibility: {
          main_access: null,
          handrails: null,
          emergency_signage: null,
          ...(d.accessibility || {}),
        },
        // Bloco 5
        tech_docs: {
          laudo_eletrico: emptyDoc(),
          laudo_gas: emptyDoc(),
          extintores: emptyDoc(),
          ...(d.tech_docs || {}),
        },
        extra_tech_docs: d.extra_tech_docs || [],
      },

      rules: {
        required: (v) => !!v || "Campo obrigatório",
        positive: (v) => !v || v > 0 || "Deve ser maior que zero",
      },
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    selectedRooms() {
      return ROOM_OPTIONS.filter((r) => this.formData.rooms.includes(r.value));
    },

    headerState() {
      const hasBasic =
        this.formData.authorized_capacity && this.formData.care_profiles.length;
      if (!hasBasic) return "pending";
      return "ok";
    },

    headerStateClass() {
      return `dossier-infra__state-icon--${this.headerState}`;
    },

    headerStateIcon() {
      return this.headerState === "ok"
        ? "fas fa-check-circle"
        : "far fa-circle";
    },
  },

  created() {
    this.loadOccupancy();
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    // Busca ocupação atual via API de residentes
    async loadOccupancy() {
      // try {
      //   this.loadingOccupancy = true;
      //   const response = await api.get(
      //     "/rest-home/dossier/infrastructure/occupancy"
      //   );
      //   const { error, current_occupancy } = response.data;
      //   if (!error) this.formData.current_occupancy = current_occupancy;
      // } catch (err) {
      //   console.error("DossierInfrastructure.loadOccupancy:", err);
      // } finally {
      //   this.loadingOccupancy = false;
      // }
    },

    // Acesso reativo aos dados de cada ambiente
    getRoomData(roomValue) {
      if (!this.formData.rooms_data[roomValue]) {
        this.$set(this.formData.rooms_data, roomValue, emptyRoomData());
      }
      return this.formData.rooms_data[roomValue];
    },

    // Upload de evidência por ambiente
    triggerRoomUpload(roomValue) {
      const ref = this.$refs[`roomUpload_${roomValue}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.click();
    },

    async handleRoomUpload(event, roomValue) {
      const file = event.target.files[0];
      if (!file) return;
      this.$set(this.formData.rooms_data[roomValue], "_uploading", true);
      try {
        const fd = new FormData();
        fd.append("file", file, file.name);
        fd.append("room", roomValue);
        const response = await api.post(
          "/rest-home/dossier/infrastructure/room/upload",
          fd
        );
        const { error, file_url, file_name } = response.data;
        if (!error) {
          this.$set(this.formData.rooms_data[roomValue], "file_url", file_url);
          this.$set(
            this.formData.rooms_data[roomValue],
            "file_name",
            file_name || file.name
          );
          toast("Evidência anexada com sucesso", "success");
        }
      } catch (err) {
        console.error("DossierInfrastructure.roomUpload:", err);
      } finally {
        this.$set(this.formData.rooms_data[roomValue], "_uploading", false);
        event.target.value = "";
      }
    },

    addOtherTechDoc() {
      this.formData.extra_tech_docs.push({ name: "", doc: emptyDoc() });
    },

    accessibilityClass(value) {
      if (value === "Sim") return "dossier-infra__access--ok";
      if (value === "Em adequação") return "dossier-infra__access--warning";
      if (value === "Não") return "dossier-infra__access--error";
      return "";
    },

    async save() {
      try {
        this.saveLoading = true;
        const payload = JSON.parse(JSON.stringify(this.formData));

        // Remove campos _ de rooms_data
        Object.keys(payload.rooms_data || {}).forEach((k) => {
          delete payload.rooms_data[k]._uploading;
        });

        const response = await api.post(
          "/rest-home/dossier/infrastructure",
          payload
        );
        const { error, errors, message } = response.data;

        if (error) {
          this.$emit("error", { module: "infrastructure", errors });
        } else {
          toast(message || "Infraestrutura salva com sucesso", "success");
          this.$emit("saved", { module: "infrastructure", data: payload });
        }
      } catch (err) {
        console.error("DossierInfrastructure.save:", err);
      } finally {
        this.saveLoading = false;
      }
    },

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER INFRAESTRUTURA
   ═══════════════════════════════════════════════════════════════ */

.dossier-infra__wrapper {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;
}
.dossier-infra__wrapper:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.dossier-infra__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}
.dossier-infra__header:hover {
  background: #f0f0ef;
}
.dossier-infra__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dossier-infra__title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.dossier-infra__subtitle {
  font-size: 12px;
  color: #888;
  display: block;
  margin-top: 2px;
}

.dossier-infra__state-icon {
  font-size: 15px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}
.dossier-infra__state-icon--ok {
  color: #30915b;
}
.dossier-infra__state-icon--pending {
  color: #ccc;
}

.dossier-infra__toggle {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #888;
  font-size: 13px;
}
.dossier-infra__toggle:hover {
  color: #1f5a76;
}

.dossier-infra__body {
  padding: 16px;
}

.dossier-infra__explainer {
  font-size: 13px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 10px 12px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.dossier-infra__explainer p {
  margin: 0;
}

/* ── Seções ──────────────────────────────────────────────────── */
.dossier-infra__section {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}

.dossier-infra__section-title {
  font-size: 12px;
  font-weight: 600;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.dossier-infra__subsection {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e0e0e0;
}
.dossier-infra__subsection-title {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
}

/* ── Controles ───────────────────────────────────────────────── */
.dossier-infra__field-group {
  margin-bottom: 12px;
}

.dossier-infra__checkboxes,
.dossier-infra__radios {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
}

.dossier-infra__checkbox-item,
.dossier-infra__radio-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}

.dossier-infra__checkbox,
.dossier-infra__radio {
  accent-color: #1f5a76;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

/* ── Ocupação automática ─────────────────────────────────────── */
.dossier-infra__auto-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.dossier-infra__occupancy {
  font-size: 20px;
  font-weight: 700;
  color: #1f5a76;
}
.dossier-infra__occupancy-total {
  font-size: 13px;
  color: #888;
}
.dossier-infra__auto-badge {
  font-size: 10px;
  background: #e8f4fd;
  color: #1f5a76;
  border: 1px solid #b8d9ec;
  border-radius: 8px;
  padding: 1px 6px;
  font-weight: 600;
  text-transform: uppercase;
}

/* ── Grid de ambientes ───────────────────────────────────────── */
.dossier-infra__rooms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  margin-top: 6px;
}

.dossier-infra__room-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}

.dossier-infra__others-field {
  margin-top: 10px;
  max-width: 400px;
}

/* ── Sub-bloco de ambiente ───────────────────────────────────── */
.dossier-infra__room-block {
  border: 1px solid #e0e0e0;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 10px;
  margin-bottom: 8px;
}

.dossier-infra__room-block-title {
  font-size: 12px;
  font-weight: 600;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
}

.dossier-infra__room-uploads {
  margin-top: 8px;
}

/* ── Arquivo ─────────────────────────────────────────────────── */
.dossier-infra__file-attached {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f4fff8;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
}

.dossier-infra__file-icon {
  font-size: 16px;
  color: #dc3545;
}
.dossier-infra__file-link {
  font-size: 12px;
  color: #1f5a76;
  font-weight: 600;
}

/* ── Acessibilidade ──────────────────────────────────────────── */
.dossier-infra__accessibility-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.dossier-infra__access-item {
  flex: 1;
  min-width: 200px;
}

.dossier-infra__access--ok {
  color: #30915b;
  font-weight: 600;
}
.dossier-infra__access--warning {
  color: #fb8b01;
  font-weight: 600;
}
.dossier-infra__access--error {
  color: #dc3545;
  font-weight: 600;
}

/* ── Documentos técnicos ─────────────────────────────────────── */
/* Recuo visual dos DossierDocumentCards filhos */
.dossier-infra__section :deep(.dossier-card) {
  border-left: 3px solid #ddd;
  margin-bottom: 6px;
}

.dossier-infra__add-doc {
  margin-top: 8px;
}
.dossier-infra__extra-doc {
  margin-top: 8px;
}

/* ── Salvar ──────────────────────────────────────────────────── */
.dossier-infra__save {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0ef;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-infra__body {
    padding: 12px;
  }
  .dossier-infra__rooms-grid {
    grid-template-columns: 1fr 1fr;
  }
  .dossier-infra__checkboxes,
  .dossier-infra__radios {
    flex-direction: column;
    gap: 8px;
  }
  .dossier-infra__accessibility-grid {
    flex-direction: column;
  }
}
</style>